import { Injectable } from '@angular/core';

/**
 * Safe Storage Service
 *
 * Fornece acesso seguro a localStorage e sessionStorage com fallback
 * para Safari private mode e outros cenários onde storage APIs são bloqueadas.
 */
@Injectable({
  providedIn: 'root',
})
export class SafeStorageService {
  private localStorageAvailable = false;
  private sessionStorageAvailable = false;
  private localMemoryFallback: Map<string, string> = new Map();
  private sessionMemoryFallback: Map<string, string> = new Map();

  constructor() {
    this.checkStorageAvailability();
  }

  private checkStorageAvailability() {
    // Testar localStorage
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      this.localStorageAvailable = true;
    } catch (e) {
      console.warn('[SafeStorage] localStorage bloqueado - usando fallback em memória');
      this.localStorageAvailable = false;
    }

    // Testar sessionStorage
    try {
      const testKey = '__storage_test__';
      sessionStorage.setItem(testKey, testKey);
      sessionStorage.removeItem(testKey);
      this.sessionStorageAvailable = true;
    } catch (e) {
      console.warn('[SafeStorage] sessionStorage bloqueado - usando fallback em memória');
      this.sessionStorageAvailable = false;
    }
  }

  // ============ LOCAL STORAGE ============

  setLocal(key: string, value: string): void {
    try {
      if (this.localStorageAvailable) {
        localStorage.setItem(key, value);
      } else {
        this.localMemoryFallback.set(key, value);
      }
    } catch (e) {
      console.error('[SafeStorage] Erro ao gravar localStorage:', e);
      this.localMemoryFallback.set(key, value);
    }
  }

  getLocal(key: string): string | null {
    try {
      if (this.localStorageAvailable) {
        return localStorage.getItem(key);
      } else {
        return this.localMemoryFallback.get(key) || null;
      }
    } catch (e) {
      console.error('[SafeStorage] Erro ao ler localStorage:', e);
      return this.localMemoryFallback.get(key) || null;
    }
  }

  removeLocal(key: string): void {
    try {
      if (this.localStorageAvailable) {
        localStorage.removeItem(key);
      } else {
        this.localMemoryFallback.delete(key);
      }
    } catch (e) {
      console.error('[SafeStorage] Erro ao remover de localStorage:', e);
      this.localMemoryFallback.delete(key);
    }
  }

  // Método genérico para limpar
  clearLocal(): void {
    if (this.isLocalStorageAvailable()) {
      console.warn('🧹 [SafeStorage] clearLocal foi chamado! A limpar localStorage.');
      localStorage.clear();
    } else {
      console.warn('🧹 [SafeStorage] clearLocal (Memory Fallback) called.');
      this.localMemoryFallback.clear();
    }
  }

  // ============ SESSION STORAGE ============

  setSession(key: string, value: string): void {
    try {
      if (this.sessionStorageAvailable) {
        sessionStorage.setItem(key, value);
      } else {
        this.sessionMemoryFallback.set(key, value);
      }
    } catch (e) {
      console.error('[SafeStorage] Erro ao gravar sessionStorage:', e);
      this.sessionMemoryFallback.set(key, value);
    }
  }

  getSession(key: string): string | null {
    try {
      if (this.sessionStorageAvailable) {
        return sessionStorage.getItem(key);
      } else {
        return this.sessionMemoryFallback.get(key) || null;
      }
    } catch (e) {
      console.error('[SafeStorage] Erro ao ler sessionStorage:', e);
      return this.sessionMemoryFallback.get(key) || null;
    }
  }

  removeSession(key: string): void {
    try {
      if (this.sessionStorageAvailable) {
        sessionStorage.removeItem(key);
      } else {
        this.sessionMemoryFallback.delete(key);
      }
    } catch (e) {
      console.error('[SafeStorage] Erro ao remover de sessionStorage:', e);
      this.sessionMemoryFallback.delete(key);
    }
  }

  clearSession(): void {
    try {
      if (this.sessionStorageAvailable) {
        sessionStorage.clear();
      } else {
        this.sessionMemoryFallback.clear();
      }
    } catch (e) {
      console.error('[SafeStorage] Erro ao limpar sessionStorage:', e);
      this.sessionMemoryFallback.clear();
    }
  }

  // ============ UTILITIES ============

  /**
   * Verifica se o storage está disponível
   */
  isLocalStorageAvailable(): boolean {
    return this.localStorageAvailable;
  }

  isSessionStorageAvailable(): boolean {
    return this.sessionStorageAvailable;
  }
}
